import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import Select from "react-select";
import { debounce } from "lodash";

export default function TeacherClassMap() {
    // State management
    const [state, setState] = useState({
        classes: [],
        teachers: [],
        schools: [],
        mappings: [],
        loading: true,
        formSubmitting: false,
        formData: {
            teacherClassMappingId: 0,
            teacherId: 0,
            schoolId: 0,
            classId: 0,
            teacherName: "",
            schoolName: "",
            className: "",
            isActive: true,
        },
        searchQuery: "",
        filterStatus: "all",
        currentPage: 0,
        itemsPerPage: 5,
    });

    useEffect(() => {
        console.log("state ================== ", state);
    }, [state]);

    // Helper function to update state
    const updateState = (newState) => {
        setState((prev) => ({ ...prev, ...newState }));
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            await Promise.all([
                fetchTeacherClassMappings(),
                fetchTeachers(),
                fetchClasses(),
                fetchSchools(),
            ]);
        } catch (error) {
            console.error("Error fetching initial data:", error);
            Swal.fire("Error!", "Failed to load initial data.", "error");
        }
    };

    // API calls
    const fetchTeachers = async () => {
        try {
            const response = await axios.get(`${API_LoginURL}user/UserList`, {
                headers: getHeaders(),
            });
            // Filter teachers (assuming roleId 2 is for teachers)
            const teachers = Array.isArray(response.data)
                ? response.data.filter((user) => user.roleId === 2 && user.isActive === false)
                : [];
            updateState({ teachers });
        } catch (error) {
            console.error("Error fetching teachers:", error);
            throw error;
        }
    };

    const fetchSchools = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}School/SchoolMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                schools: Array.isArray(response.data) ? response.data : [],
            });
        } catch (error) {
            console.error("Error fetching schools:", error);
            throw error;
        }
    };

    const fetchClasses = async () => {
        try {
            const response = await axios.get(
                `${API_LoginURL}class/ClassMasterList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                classes: Array.isArray(response.data) ? response.data : [],
            });
        } catch (error) {
            console.error("Error fetching classes:", error);
            throw error;
        }
    };

    const fetchTeacherClassMappings = async () => {
        updateState({ loading: true });
        try {
            const response = await axios.get(
                `${API_LoginURL}TeacherClassMapping/TeacherClassMappingDetailList`,
                {
                    headers: getHeaders(),
                }
            );
            updateState({
                mappings: Array.isArray(response.data) ? response.data : [],
                loading: false,
            });
        } catch (error) {
            console.error("Error fetching teacher-class mappings:", error);
            Swal.fire(
                "Error!",
                "Failed to load teacher-class mappings.",
                "error"
            );
            updateState({ loading: false });
            throw error;
        }
    };

    // Helper function for headers
    const getHeaders = () => ({
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("token"),
    });

    // Form handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateState({
            formData: {
                ...state.formData,
                [name]: value,
            },
        });
    };

    const handleTeacherChange = (selectedOption) => {
        const teacherId = selectedOption.value;
        const teacherName = selectedOption.label;

        const { schoollId, schoolName } = state?.teachers?.find(
            (teacher) => teacher.userId === teacherId
        );

        updateState({
            formData: {
                ...state.formData,
                teacherId: selectedOption ? selectedOption.value : 0,
                teacherName: selectedOption ? selectedOption.label : "",
                schoolId: schoollId,
                schoolName: schoolName,
            },
        });
    };

    const handleSchoolChange = (selectedOption) => {
        updateState({
            formData: {
                ...state.formData,
                schoolId: selectedOption ? selectedOption.value : 0,
                schoolName: selectedOption ? selectedOption.label : "",
            },
        });
    };

    const handleClassChange = (selectedOption) => {
        updateState({
            formData: {
                ...state.formData,
                classId: selectedOption ? selectedOption.value : 0,
                className: selectedOption ? selectedOption.label : "",
            },
        });
    };

    // Debounced search
    const debouncedSearch = useMemo(
        () =>
            debounce((query) => {
                updateState({ searchQuery: query, currentPage: 0 });
            }, 300),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleFilterChange = (e) => {
        updateState({
            filterStatus: e.target.value,
            currentPage: 0,
        });
    };

    const resetForm = () => {
        updateState({
            formData: {
                teacherClassMappingId: 0,
                teacherId: 0,
                schoolId: 0,
                classId: 0,
                teacherName: "",
                schoolName: "",
                className: "",
                isActive: true,
            },
        });
    };

    const handleEdit = (mapping) => {
        updateState({
            formData: {
                teacherClassMappingId: mapping.teacherClassMappingId,
                teacherId: mapping.teacherId || 0,
                schoolId: mapping.schoolId || 0,
                classId: mapping.classId || 0,
                teacherName: mapping.teacherName || "",
                schoolName: mapping.schoolName || "",
                className: mapping.className || "",
                isActive: mapping.isActive !== false,
            },
        });
    };

    const handleStatusChange = async (teacherClassMappingId, currentStatus) => {
        const confirmation = await Swal.fire({
            title: "Are you sure?",
            text: `Do you want to ${
                currentStatus ? "deactivate" : "activate"
            } this mapping?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `Yes, ${
                currentStatus ? "deactivate" : "activate"
            } it!`,
        });

        if (!confirmation.isConfirmed) return;

        try {
            await axios.post(
                `${API_LoginURL}TeacherClassMapping/ActiveInactiveTeacheClassMappingDetails`,
                { teacherClassMappingId },
                { headers: getHeaders() }
            );

            // Update local state
            updateState({
                mappings: state.mappings.map((item) =>
                    item.teacherClassMappingId === teacherClassMappingId
                        ? { ...item, isActive: !currentStatus }
                        : item
                ),
            });

            Swal.fire(
                "Success!",
                `Mapping has been ${
                    currentStatus ? "deactivated" : "activated"
                }.`,
                "success"
            );
        } catch (error) {
            console.error("Error changing mapping status:", error);
            Swal.fire("Error", "Failed to change mapping status", "error");
        }
    };

    const handlePageChange = ({ selected }) => {
        updateState({ currentPage: selected });
    };

    const validateForm = () => {
        if (!state.formData.teacherId) {
            Swal.fire("Warning", "Please select a teacher", "warning");
            return false;
        }
        if (!state.formData.schoolId) {
            Swal.fire("Warning", "Please select a school", "warning");
            return false;
        }
        if (!state.formData.classId) {
            Swal.fire("Warning", "Please select a class", "warning");
            return false;
        }
        return true;
    };

    const handleFormSubmit = async () => {
        if (!validateForm()) return;

        updateState({ formSubmitting: true });
        try {
            const payload = {
                teacherClassMappingId: state.formData.teacherClassMappingId,
                teacherId: state.formData.teacherId,
                schoolId: state.formData.schoolId,
                classId: state.formData.classId,
                isActive: state.formData.isActive,
            };

            const url = `${API_LoginURL}TeacherClassMapping/TeacherClassMappingAddEdit`;
            const response = await axios.post(url, payload, {
                headers: getHeaders(),
            });

            if (response.data.response === "SUCCESS") {
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text:
                        state.formData.teacherClassMappingId > 0
                            ? "Mapping updated successfully"
                            : "Mapping added successfully",
                });
                await fetchTeacherClassMappings();
                document.getElementById("closeMappingModal").click();
                resetForm();
            } else {
                throw new Error(
                    response.data.message || "Failed to save mapping"
                );
            }
        } catch (error) {
            console.error("Error saving mapping:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text:
                    error.response?.data?.message ||
                    error.message ||
                    "Failed to save mapping",
            });
        } finally {
            updateState({ formSubmitting: false });
        }
    };

    // Filter and pagination
    const filteredMappings = state.mappings.filter((item) => {
        const matchesSearch =
            item.teacherName
                ?.toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            item.schoolName
                ?.toLowerCase()
                .includes(state.searchQuery.toLowerCase()) ||
            item.className
                ?.toLowerCase()
                .includes(state.searchQuery.toLowerCase());

        const matchesStatus =
            state.filterStatus === "all" ||
            (state.filterStatus === "active" && item.isActive) ||
            (state.filterStatus === "inactive" && !item.isActive);

        return matchesSearch && matchesStatus;
    });

    const pageCount = Math.ceil(filteredMappings.length / state.itemsPerPage);
    const displayedMappings = filteredMappings.slice(
        state.currentPage * state.itemsPerPage,
        (state.currentPage + 1) * state.itemsPerPage
    );

    // Prepare select options
    const teacherOptions = state.teachers.map((teacher) => ({
        value: teacher.userId,
        label: `${teacher.firstName} ${teacher.lastName}`,
    }));

    const schoolOptions = state.schools.map((school) => ({
        value: school.school_id, // Changed from schoolId to school_id
        label: school.school_name, // Changed from schoolName to school_name
    }));

    const classOptions = state.classes.map((cls) => ({
        value: cls.classId,
        label: cls.className,
    }));

    // Get current selected values for the form
    const selectedTeacher = teacherOptions.find(
        (option) => option.value === state.formData.teacherId
    );
    const selectedSchool = schoolOptions.find(
        (option) => option.value === state.formData.schoolId
    );
    const selectedClass = classOptions.find(
        (option) => option.value === state.formData.classId
    );

    return (
        <div className="container-scroller">
            <Navbar />
            <div className="container-fluid page-body-wrapper">
                <SASidebar />
                <div className="main-panel">
                    <div className="content-wrapper">
                        <div className="col-lg-12 grid-margin stretch-card">
                            <div className="card">
                                <div className="card-body">
                                    {/* Header Section */}
                                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                                        <h4 className="card-title mb-3 mb-md-0">
                                            Teacher-Class Mapping
                                        </h4>

                                        {/* Search and Filter */}
                                        <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                                            {/* Search */}
                                            <div
                                                className="flex-grow-1 me-md-2 position-relative"
                                                style={{ maxWidth: "500px" }}
                                            >
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm ps-5 rounded-pill border-0"
                                                    placeholder="Search by Teacher, School or Class"
                                                    onChange={
                                                        handleSearchChange
                                                    }
                                                    style={{
                                                        height: "38px",
                                                        boxShadow:
                                                            "0 2px 8px rgba(0,0,0,0.08)",
                                                        transition:
                                                            "all 0.3s ease",
                                                        backgroundColor:
                                                            "#f8f9fa",
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 12px rgba(0,0,0,0.15)";
                                                        e.target.style.backgroundColor =
                                                            "#fff";
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.boxShadow =
                                                            "0 2px 8px rgba(0,0,0,0.08)";
                                                        e.target.style.backgroundColor =
                                                            "#f8f9fa";
                                                    }}
                                                />
                                                <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                                            </div>

                                            {/* Filter */}
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-filter me-2 text-muted"></i>
                                                <select
                                                    className="form-select"
                                                    value={state.filterStatus}
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                    style={{
                                                        minWidth: "120px",
                                                    }}
                                                >
                                                    <option value="all">
                                                        All Status
                                                    </option>
                                                    <option value="active">
                                                        Active
                                                    </option>
                                                    <option value="inactive">
                                                        Inactive
                                                    </option>
                                                </select>
                                            </div>

                                            {/* Add Mapping Button */}
                                            <button
                                                className="btn btn-primary d-flex align-items-center"
                                                data-bs-toggle="modal"
                                                data-bs-target="#mappingModal"
                                                onClick={resetForm}
                                            >
                                                <i className="fas fa-plus me-2"></i>
                                                <span className="d-none d-md-inline">
                                                    Add Mapping
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mappings Table */}
                                    <div className="table-responsive">
                                        {state.loading ? (
                                            <div
                                                className="d-flex justify-content-center align-items-center"
                                                style={{ height: "200px" }}
                                            >
                                                <RotatingLines
                                                    color="#4e73df"
                                                    size={50}
                                                />
                                                <p className="mt-3 ms-3">
                                                    Loading mappings...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                <table className="table table-hover table-striped">
                                                    <thead className="table-primary">
                                                        <tr>
                                                            <th width="5%">
                                                                ID
                                                            </th>
                                                            <th width="20%">
                                                                Teacher
                                                            </th>
                                                            <th width="20%">
                                                                School
                                                            </th>
                                                            <th width="20%">
                                                                Class
                                                            </th>
                                                            <th width="10%">
                                                                Status
                                                            </th>
                                                            <th
                                                                width="25%"
                                                                className="text-end"
                                                            >
                                                                Actions
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayedMappings.length >
                                                        0 ? (
                                                            displayedMappings.map(
                                                                (item) => (
                                                                    <tr
                                                                        key={
                                                                            item.teacherClassMappingId
                                                                        }
                                                                    >
                                                                        <td>
                                                                            {
                                                                                item.teacherClassMappingId
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.teacherName
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.schoolName
                                                                            }
                                                                        </td>
                                                                        <td>
                                                                            {
                                                                                item.className
                                                                            }
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                color: item.isActive
                                                                                    ? "green"
                                                                                    : "red",
                                                                            }}
                                                                        >
                                                                            {item.isActive
                                                                                ? "Active"
                                                                                : "Inactive"}
                                                                        </td>
                                                                        <td className="text-end">
                                                                            <div className="d-flex justify-content-end gap-2">
                                                                                <button
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    data-bs-toggle="modal"
                                                                                    data-bs-target="#mappingModal"
                                                                                    onClick={() =>
                                                                                        handleEdit(
                                                                                            item
                                                                                        )
                                                                                    }
                                                                                    title="Edit"
                                                                                >
                                                                                    <i className="fas fa-edit"></i>
                                                                                </button>
                                                                                <button
                                                                                    className={`btn btn-sm ${
                                                                                        item.isActive
                                                                                            ? "btn-outline-danger"
                                                                                            : "btn-outline-success"
                                                                                    }`}
                                                                                    onClick={() =>
                                                                                        handleStatusChange(
                                                                                            item.teacherClassMappingId,
                                                                                            item.isActive
                                                                                        )
                                                                                    }
                                                                                    title={
                                                                                        item.isActive
                                                                                            ? "Deactivate"
                                                                                            : "Activate"
                                                                                    }
                                                                                >
                                                                                    <i
                                                                                        className={`fas ${
                                                                                            item.isActive
                                                                                                ? "fa-times"
                                                                                                : "fa-check"
                                                                                        }`}
                                                                                    ></i>
                                                                                </button>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            )
                                                        ) : (
                                                            <tr>
                                                                <td
                                                                    colSpan="6"
                                                                    className="text-center py-4"
                                                                >
                                                                    <div className="d-flex flex-column align-items-center">
                                                                        <img
                                                                            src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                                                            alt="No mappings"
                                                                            style={{
                                                                                opacity: 0.5,
                                                                                width: "60px",
                                                                            }}
                                                                        />
                                                                        <p className="mt-3 text-muted">
                                                                            No
                                                                            mappings
                                                                            found
                                                                        </p>
                                                                        <button
                                                                            className="btn btn-sm btn-primary mt-2"
                                                                            data-bs-toggle="modal"
                                                                            data-bs-target="#mappingModal"
                                                                            onClick={
                                                                                resetForm
                                                                            }
                                                                        >
                                                                            Add
                                                                            New
                                                                            Mapping
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>

                                                {/* Pagination */}
                                                {displayedMappings.length >
                                                    0 && (
                                                    <ReactPaginate
                                                        previousLabel={
                                                            <i className="fas fa-chevron-left"></i>
                                                        }
                                                        nextLabel={
                                                            <i className="fas fa-chevron-right"></i>
                                                        }
                                                        pageCount={pageCount}
                                                        onPageChange={
                                                            handlePageChange
                                                        }
                                                        containerClassName={
                                                            "pagination justify-content-center mt-4"
                                                        }
                                                        pageClassName={
                                                            "page-item"
                                                        }
                                                        pageLinkClassName={
                                                            "page-link"
                                                        }
                                                        previousClassName={
                                                            "page-item"
                                                        }
                                                        previousLinkClassName={
                                                            "page-link"
                                                        }
                                                        nextClassName={
                                                            "page-item"
                                                        }
                                                        nextLinkClassName={
                                                            "page-link"
                                                        }
                                                        activeClassName={
                                                            "active"
                                                        }
                                                        breakLabel={"..."}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        forcePage={
                                                            state.currentPage
                                                        }
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mapping Modal */}
            <div
                className="modal fade"
                id="mappingModal"
                tabIndex="-1"
                aria-labelledby="mappingModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header bg-light">
                            <h5 className="modal-title" id="mappingModalLabel">
                                {state.formData.teacherClassMappingId > 0
                                    ? "Edit Mapping"
                                    : "Add New Mapping"}
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="row">
                                <div className="col-md-6 pe-0 mb-3">
                                    <label
                                        htmlFor="teacherId"
                                        className="form-label required"
                                    >
                                        Teacher
                                    </label>
                                    <Select
                                        id="teacherId"
                                        options={teacherOptions}
                                        value={selectedTeacher}
                                        onChange={handleTeacherChange}
                                        placeholder="Select Teacher"
                                        className="basic-single"
                                        classNamePrefix="select"
                                        // isClearable
                                        isDisabled={
                                            state.formData
                                                .teacherClassMappingId > 0
                                                ? true
                                                : false
                                        }
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: "40px",
                                            }),
                                        }}
                                    />
                                </div>
                                
                                <div className="col-md-5 flex-grow-1 ps-0 mb-3">
                                    <label
                                        htmlFor="classId"
                                        className="form-label required"
                                    >
                                        Class
                                    </label>
                                    <Select
                                        id="classId"
                                        options={classOptions}
                                        value={selectedClass}
                                        onChange={handleClassChange}
                                        placeholder="Select Class"
                                        className="basic-single"
                                        classNamePrefix="select"
                                        isClearable
                                        isSearchable
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: "40px",
                                            }),
                                        }}
                                    />
                                </div>
                                </div>
                                <div className="row">
                                <div className="mb-3">
                                    <label
                                        htmlFor="schoolId"
                                        className="form-label required"
                                    >
                                        School
                                    </label>
                                    <Select
                                        id="schoolId"
                                        options={schoolOptions}
                                        value={selectedSchool}
                                        // onChange={handleSchoolChange}
                                        placeholder="School Value"
                                        className="basic-single"
                                        classNamePrefix="select"
                                        // isClearable
                                        // isSearchable
                                        isDisabled
                                        menuPortalTarget={document.body}
                                        styles={{
                                            menuPortal: (base) => ({
                                                ...base,
                                                zIndex: 9999,
                                            }),
                                            control: (provided) => ({
                                                ...provided,
                                                minHeight: "40px",
                                            }),
                                        }}
                                    />
                                </div>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                id="closeMappingModal"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                                disabled={state.formSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleFormSubmit}
                                disabled={state.formSubmitting}
                            >
                                {state.formSubmitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Submitting...
                                    </>
                                ) : state.formData.teacherClassMappingId > 0 ? (
                                    "Update Mapping"
                                ) : (
                                    "Add Mapping"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
